import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";

export default function WalkinModal({ open, onClose, onSuccess }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [mode, setMode] = useState("opd");
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [activeDate, setActiveDate] = useState(null);
  const [dates, setDates] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/doctors?limit=100").then((r) => setDoctors(r.data.items || []));
  }, [open]);

  useEffect(() => {
    if (!doctorId) {
      setSlots([]); setDates([]); setActiveDate(null); setSlotId("");
      return;
    }
    api.get(`/doctors/${doctorId}`).then((r) => {
      const ds = r.data.dates.slice(0, 3); // today + 2
      setDates(ds);
      setActiveDate(ds[0]);
      const merged = ds.flatMap((d) =>
        (r.data.slots_by_date[d] || []).map((s) => ({ ...s, _date: d })),
      );
      setSlots(merged);
      setSlotId("");
    });
  }, [doctorId]);

  const submit = async () => {
    if (!phone.match(/^\+91[6-9]\d{9}$/)) {
      toast.error("Phone must be +91XXXXXXXXXX");
      return;
    }
    if (!doctorId || !slotId) {
      toast.error("Select doctor and slot");
      return;
    }
    setBusy(true);
    try {
      await api.post("/clinic/me/walkin-book", {
        patient_phone: phone,
        patient_name: name || undefined,
        doctor_id: doctorId,
        slot_id: slotId,
        mode,
      });
      toast.success("Walk-in appointment created");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const slotsForActiveDate = slots.filter((s) => s._date === activeDate && s.mode_allowed.includes(mode));

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent data-testid="walkin-modal" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle>Add walk-in patient</DialogTitle>
          <DialogDescription>Book an appointment on behalf of a patient at the front desk.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Patient phone</Label>
            <Input
              data-testid="walkin-phone-input"
              placeholder="+919000000001"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Patient name (optional)</Label>
            <Input
              data-testid="walkin-name-input"
              placeholder="If patient is new"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Doctor</Label>
              <Select value={doctorId} onValueChange={setDoctorId}>
                <SelectTrigger data-testid="walkin-doctor-select" className="mt-1">
                  <SelectValue placeholder="Choose doctor" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`walkin-doctor-option-${d.id}`}>
                      {d.name} · {d.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="walkin-mode-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="opd">OPD</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {dates.length > 0 && (
            <>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {dates.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setActiveDate(d); setSlotId(""); }}
                    data-testid={`walkin-date-${d}`}
                    className={
                      "shrink-0 px-3 py-2 rounded-xl border text-xs " +
                      (d === activeDate
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-white hover:border-primary/40")
                    }
                  >
                    {new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto" data-testid="walkin-slot-grid">
                {slotsForActiveDate.length === 0 ? (
                  <div className="col-span-4 text-xs text-muted-foreground text-center py-4">
                    No {mode} slots on this day.
                  </div>
                ) : (
                  slotsForActiveDate.map((s) => {
                    const disabled = s.is_booked;
                    const selected = slotId === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => setSlotId(s.id)}
                        data-testid={`walkin-slot-${s.id}`}
                        className={
                          "h-10 rounded-xl text-xs font-medium border " +
                          (disabled
                            ? "bg-muted text-muted-foreground line-through cursor-not-allowed"
                            : selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-white hover:border-primary/40")
                        }
                      >
                        {s.start_time}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button
              data-testid="walkin-submit-btn"
              onClick={submit}
              disabled={busy || !slotId}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground"
            >
              {busy ? "Booking…" : "Confirm walk-in"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
